import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import {
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material';
import { api } from '../services/api';
import { Highlight } from '../types';
import HighlightModal from './HighlightModal';

const ICHighlightsSection: React.FC = () => {
  const [highlights, setHighlights] = useState<Highlight[]>([]);
  const [filteredHighlights, setFilteredHighlights] = useState<Highlight[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingHighlight, setEditingHighlight] = useState<Highlight | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const MAX_HIGHLIGHTS = 10;

  useEffect(() => {
    // Fetch highlights on mount
    fetchHighlights();
  }, []);

  useEffect(() => {
    const filtered = highlights.filter(
      (highlight) =>
        highlight.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        format(new Date(highlight.date), 'MMMM d, yyyy').toLowerCase().includes(searchTerm.toLowerCase()) ||
        format(new Date(highlight.date), 'yyyy-MM-dd').includes(searchTerm),
    );
    setFilteredHighlights(filtered);
  }, [highlights, searchTerm]);

  const fetchHighlights = async () => {
    try {
      console.log('Fetching highlights for authenticated user');
      const data = await api.getHighlights();
      console.log('Fetched highlights:', data);
      setHighlights(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching highlights:', error);
      alert('Error fetching highlights: ' + (error instanceof Error ? error.message : String(error)));
      setLoading(false);
    }
  };

  const handleAdd = () => {
    if (highlights.length >= MAX_HIGHLIGHTS) {
      alert(`You can only have a maximum of ${MAX_HIGHLIGHTS} customer highlights.`);
      return;
    }
    setEditingHighlight(null);
    setIsModalOpen(true);
  };

  const handleEdit = (highlight: Highlight) => {
    setEditingHighlight(highlight);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this highlight?')) {
      try {
        await api.deleteHighlight(id);
        fetchHighlights();
      } catch (error) {
        console.error('Error deleting highlight:', error);
      }
    }
  };

  const handleSave = async (highlightData: Omit<Highlight, 'id'>) => {
    console.log('Saving highlight data:', highlightData);
    setSaving(true);
    try {
      if (editingHighlight) {
        console.log('Updating existing highlight:', editingHighlight.id);
        await api.updateHighlight(editingHighlight.id, highlightData);
      } else {
        console.log('Creating new highlight');
        const result = await api.createHighlight(highlightData);
        console.log('Created highlight:', result);
      }
      setIsModalOpen(false);
      await fetchHighlights();
    } catch (error) {
      console.error('Error saving highlight:', error);
      alert('Error saving highlight: ' + (error instanceof Error ? error.message : String(error)));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h2" gutterBottom>
          Customer Highlights
        </Typography>
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Loading customer highlights...
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h2">
          Customer Highlights ({highlights.length}/{MAX_HIGHLIGHTS})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          size="small"
          disabled={highlights.length >= MAX_HIGHLIGHTS}
        >
          Add
        </Button>
      </Box>

      {highlights.length >= MAX_HIGHLIGHTS && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You have reached the maximum of {MAX_HIGHLIGHTS} customer highlights. Delete an existing highlight to add a
          new one.
        </Alert>
      )}

      <TextField
        fullWidth
        placeholder="Search customer highlights..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
        }}
      />

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ minHeight: '48px' }}>
              <TableCell sx={{ minWidth: '160px', resize: 'horizontal', overflow: 'auto', minHeight: '48px' }}>
                Activity Date
              </TableCell>
              <TableCell sx={{ minWidth: '100px', resize: 'horizontal', overflow: 'auto', minHeight: '48px' }}>
                Alias
              </TableCell>
              <TableCell sx={{ minWidth: '120px', resize: 'horizontal', overflow: 'auto', minHeight: '48px' }}>
                Manager Alias
              </TableCell>
              <TableCell sx={{ minWidth: '200px', resize: 'horizontal', overflow: 'auto', minHeight: '48px' }}>
                Description
              </TableCell>
              <TableCell
                sx={{
                  minWidth: '120px',
                  textAlign: 'center',
                  resize: 'horizontal',
                  overflow: 'auto',
                  minHeight: '48px',
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredHighlights.map((highlight) => (
              <TableRow key={highlight.id} hover sx={{ resize: 'vertical', overflow: 'auto', minHeight: '60px' }}>
                <TableCell sx={{ minHeight: '60px', verticalAlign: 'top' }}>
                  {format(new Date(highlight.date), 'MMMM d, yyyy')}
                </TableCell>
                <TableCell sx={{ minHeight: '60px', verticalAlign: 'top' }}>{highlight.alias}</TableCell>
                <TableCell sx={{ minHeight: '60px', verticalAlign: 'top' }}>{highlight.manager_alias}</TableCell>
                <TableCell
                  sx={{
                    wordWrap: 'break-word',
                    whiteSpace: 'normal',
                    lineHeight: 1.4,
                    py: 2,
                    minHeight: '60px',
                    verticalAlign: 'top',
                  }}
                >
                  {highlight.description}
                </TableCell>
                <TableCell sx={{ textAlign: 'center', minHeight: '60px', verticalAlign: 'middle' }}>
                  <IconButton size="small" onClick={() => handleEdit(highlight)} color="primary">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(highlight.id)} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredHighlights.length === 0 && (
        <Box textAlign="center" py={4}>
          <Typography variant="body2" color="text.secondary">
            No customer highlights found.{searchTerm && ' Try adjusting your search terms.'}
          </Typography>
        </Box>
      )}

      {isModalOpen && (
        <HighlightModal
          highlight={editingHighlight}
          onSave={handleSave}
          onClose={() => setIsModalOpen(false)}
          saving={saving}
        />
      )}
    </Paper>
  );
};

export default ICHighlightsSection;
